<template>
  <modal
    class="search-admin-modal"
    :show.sync="localDisplay"
    :close-icon="false"
    body-classes="p-0"
  >
    <div
      class="search-admin-wrapper"
      v-click-outside="handleClickOutside"
      @click.capture="handleClickInside"
    >
      <SearchInput
        ref="search-input"
        class="search-input"
        :tags="selectedTags"
        v-model="searchInputFilter"
        :autocomplete="searchAutocompleteContent"
        @focus="showDropdown = true"
        @delete-tag="deleteTag"
        @delete-all="deleteAllFilters"
      />
      <LoaderLine :loading="loading" />

      <div v-if="showDropdown" class="search-dropdown">
        <div class="d-flex ml-3 pt-2" v-if="searchCorrection.text">
          <div>{{ $t('knowledge.search.correction') }}</div>
          <div
            @click.stop="searchInputFilter = searchCorrection.text"
            class="search-correction"
            v-html="sanitize(searchCorrection.highlighted)"
          ></div>
        </div>
        <div class="d-flex ml-3 pt-2" v-if="displayFilterSuggestion">
          <div>{{ $t('knowledge.search.suggestion-filter') }}</div>
          <div class="ml-2">
            <Tag :tag="searchFilterSuggestion" @focus="useSuggestFilter" />
          </div>
        </div>
        <SearchHeader
          :display="showToggleMoreFilters"
          :toggle="toggleMoreFilters"
          :title="$t('knowledge.search-admin.search')"
          @toggle-filters="toggleMoreFilters = !toggleMoreFilters"
        />
        <div class="overflow">
          <SearchFilters
            :selected-tags="selectedTags"
            :show-more-filters="toggleMoreFilters"
            @update-selected-tags="updateTags"
            @set-dates="handleDates"
          />
          <SearchRecentlyConsulted
            v-if="displayRecentlyConsulted"
            :contents="
              orderedUserRecentlyConsultedContents(focusKnowledgeValue)
            "
            @go-to-document="goToDocument"
          />
          <SearchSuggestions
            v-loading="loading"
            v-if="displaySearchSuggestions"
            class="search-suggestions"
            :results="results"
            :labelSearchedIds="labelSearchedIds"
            @go-to-document="goToDocument"
          />
        </div>
        <SearchFooter
          v-if="searchInProgress"
          :loading="loading"
          :steps="searchSteps"
          :count="totalCount"
          @go-to-results-page="goToSearchResultPage"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import debounce from 'lodash.debounce';
import { v4 as uuid } from 'uuid';
import { resolveSuggestFilter } from './ResolveSuggestFilter';

import SearchInput from './SearchInput';
import LoaderLine from './LoaderLine';
import SearchHeader from './SearchHeader';
import SearchFilters from './SearchFilters';
import SearchSuggestions from './SearchSuggestions';
import SearchRecentlyConsulted from './SearchRecentlyConsulted';
import SearchFooter from './SearchFooter';
import { sanitize } from '@/plugins/dompurify';
import Tag from './Tag';

export default {
  name: 'search-wrapper',
  components: {
    SearchInput,
    LoaderLine,
    SearchFilters,
    SearchSuggestions,
    SearchRecentlyConsulted,
    SearchFooter,
    SearchHeader,
    Tag,
  },
  props: {
    display: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      suggest: {
        corrections: null,
        autocomplete: null,
        filter: null,
      },
      sanitize: sanitize,
      localDisplay: this.display,
      showDropdown: false,
      toggleMoreFilters: false,
      searchInputFilter: '',
      selectedTags: [],
      loading: false,
      results: [],
      totalCount: 0,
      searchFrom: 0,
      size: 10,
      correlationId: 'admin-search-bar-' + uuid(),
      debounceSearchFilters: debounce(() => {
        this.superSearch();
      }, 300),
      debounceSearchAutocomplete: debounce(() => {
        this.handleSearchAutocomplete();
      }, 100),
      dates: { createdAt: {} },
    };
  },
  mounted() {
    document.addEventListener('keydown', this._keyListener.bind(this));
  },
  watch: {
    searchInputFilter() {
      if (this.selectedTags.length || this.searchInputFilter) {
        this.loading = true;
        this.emptySuggest();
        this.debounceSearchAutocomplete();
        this.debounceSearchFilters();
      } else {
        this.toggleMoreFilters = false;
        this.results = [];
        this.totalCount = 0;
      }
    },
    chosenEntity() {
      this.toggleMoreFilters =
        this.chosenEntity !== 'attached-contents' && this.isParametric
          ? false
          : this.toggleMoreFilters;
    },
    selectedTags() {
      if (!(this.selectedTags.length || this.searchInputFilter)) {
        this.results = [];
        this.totalCount = 0;
      } else {
        this.loading = true;
        this.debounceSearchAutocomplete();
        this.debounceSearchFilters();
      }
    },
    localDisplay() {
      if (!this.localDisplay) {
        this.$emit('close');
      }
    },
  },
  computed: {
    searchCorrection() {
      if (
        !this.suggest.corrections ||
        !this.suggest.corrections.text ||
        !this.searchInputFilter
      )
        return { highlighted: '', text: '' };
      return this.suggest.corrections;
    },
    searchFilterSuggestion() {
      if (this.noFilterSuggestion) return {};
      return resolveSuggestFilter(this.suggest.filter);
    },
    noFilterSuggestion() {
      return (
        !this.suggest.filter ||
        !this.suggest.filter.text ||
        !this.searchInputFilter
      );
    },
    displayFilterSuggestion() {
      return this.searchFilterSuggestion && !this.noFilterSuggestion;
    },
    searchAutocompleteContent() {
      if (
        !this.suggest.autocomplete ||
        !this.suggest.autocomplete.label ||
        !this.suggest.autocomplete.remaining ||
        !this.searchInputFilter ||
        this.searchInputFilter === this.suggest.autocomplete.label
      )
        return { id: '', label: '', type: '', remaining: '' };
      return this.suggest.autocomplete;
    },
    chosenEntity() {
      const entity = this.selectedTags.find((tag) => tag.key === 'entity');
      return entity ? entity.value : '';
    },
    entitiesSearchField() {
      if (this.isParametric) {
        return this.chosenEntity === 'attached-contents' ||
          this.chosenEntity === 'new-products'
          ? [this.chosenEntity]
          : ['attached-contents', 'cases', 'new-products'];
      } else return ['attached-contents', 'cases'];
    },
    preFilters() {
      return this.selectedTags.reduce((acc, tag) => {
        if (tag.key !== 'entity') {
          if (acc[tag.key]) {
            acc[tag.key].push(tag.value);
          } else {
            acc[tag.key] = [tag.value];
          }
        }
        return acc;
      }, {});
    },
    isProductEntity() {
      return this.chosenEntity === 'new-products';
    },
    hasUnviversTag() {
      return this.selectedTags.map((t) => t.key).includes('knowledge');
    },
    hasFamilyTag() {
      return this.selectedTags.map((t) => t.key).includes('family');
    },
    isUniversStep() {
      return this.isProductEntity && !this.hasUnviversTag;
    },
    isfamilyStep() {
      return this.isProductEntity && this.hasUnviversTag && !this.hasFamilyTag;
    },
    searchSteps() {
      return {
        univers: this.isUniversStep,
        family: this.isfamilyStep,
      };
    },
    searchInProgress() {
      return this.selectedTags.length > 0 || this.searchInputFilter !== '';
    },
    queryParameters() {
      return {
        search: this.searchInputFilter,
        ...this.selectedTags.reduce((acc, tag) => {
          if (acc[tag.key]) {
            acc[tag.key].push(tag.value);
          } else {
            acc[tag.key] = [tag.value];
          }
          return acc;
        }, {}),
        init: true,
        correlation_id: this.correlationId,
      };
    },
    showToggleMoreFilters() {
      return this.chosenEntity === 'attached-contents' || !this.isParametric;
    },
    labelSearchedIds() {
      return this.selectedTags.reduce((acc, tag) => {
        if (tag.key === 'categories') acc.push(tag.id);
        return acc;
      }, []);
    },
    noAutocomplete() {
      return !this.companySearchAutocompletePreference || this.isParametric;
    },
    displayRecentlyConsulted() {
      return (
        (!this.results ||
          !this.results.length ||
          this.searchInputFilter === '') &&
        this.orderedUserRecentlyConsultedContents(this.focusKnowledgeValue) &&
        this.orderedUserRecentlyConsultedContents(this.focusKnowledgeValue)
          .length
      );
    },
    displaySearchSuggestions() {
      return this.results && this.results.length;
    },
    ...mapGetters([
      'isParametric',
      'companySearchAutocompletePreference',
      'orderedUserRecentlyConsultedContents',
    ]),
    ...mapState(['accessToken']),
    ...mapGetters('knowledgeModule', [
      'editingLanguage',
      'focusKnowledgeValue',
    ]),
  },
  methods: {
    ...mapActions(['searchAction']),
    ...mapActions('knowledgeModule', [
      'searchAutocomplete',
      'goToTranslatedEntity',
    ]),
    updateTags(tags) {
      this.selectedTags = tags;
    },
    deleteTag(tag) {
      const index = this.selectedTags.findIndex(
        (t) => t.id == tag.id && t.key == tag.key,
      );
      this.selectedTags.splice(index, 1);
      if (tag.key === 'entity') {
        this.selectedTags = [];
      } else if (tag.key === 'knowledge') {
        this.selectedTags = this.selectedTags.filter((t) => t.key === 'entity');
      } else if (tag.key === 'family') {
        this.selectedTags = this.selectedTags.filter(
          (t) => t.key === 'entity' || t.key === 'knowledge',
        );
      }
    },
    deleteAllFilters() {
      this.searchInputFilter = '';
      this.selectedTags = [];
    },
    async handleSearchAutocomplete() {
      if (this.noAutocomplete) return;
      const suggest = await this.searchAutocomplete({
        queryText: this.searchInputFilter,
        pre: {
          ...this.preFilters,
          ...this.dates,
        },
      });
      if (!suggest) return;
      this.suggest = suggest;
    },
    useSuggestFilter(val) {
      this.selectedTags.push(val);
      this.searchInputFilter = '';
    },
    emptySuggest() {
      this.suggest = {
        corrections: null,
        autocomplete: null,
        filter: null,
      };
    },
    async superSearch() {
      try {
        const resp = await this.searchAction({
          searchType: 'superSearch',
          searchArgs: {
            entity: this.entitiesSearchField,
            fields: ['entity', 'type'],
            searchFrom: this.searchFrom,
            size: this.size,
            correlationId: this.correlationId,
            lang: this.editingLanguage,
            payload: {
              queryText: this.searchInputFilter,
              pre: {
                ...this.preFilters,
                ...this.dates,
              },
              includeCorrections: true,
            },
          },
        });

        this.results = resp.documents;
        this.totalCount = resp.counts.totalCount;
        if (!this.noAutocomplete) this.suggest.corrections = resp.correction;
        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    goToSearchResultPage() {
      this.$router.push({
        name: 'knowledge-filter',
        query: this.queryParameters,
        params: { lang: this.editingLanguage },
      });
      this.localDisplay = false;
    },
    goToDocument(doc) {
      let entityId = doc.id,
        entityType = doc.type || doc.__typename, // Product and Case have no type field and use __typename (graphql)
        parentId;
      const query = { from: 'search' };

      if (['Step', 'keyStep'].includes(entityType)) {
        parentId = doc.ancestors[0].split('/')[0];
      }

      this.goToTranslatedEntity({ entityId, entityType, parentId, query });

      this.localDisplay = false;
    },
    handleDates(dates) {
      this.dates = dates;
      this.debounceSearchFilters();
    },
    handleClickOutside() {
      this.localDisplay = false;
    },
    handleClickInside() {
      this.$refs['search-input'].focusInput();
    },
    beforeDestroy() {
      this.debounceSearchFilters.flush();
    },
    _keyListener(e) {
      if (e.key === 'Tab' && this.displayFilterSuggestion) {
        e.preventDefault();
        this.$refs['search-input'].focusInput();
        this.useSuggestFilter(this.searchFilterSuggestion);
      }
      if (
        e.key === 'Backspace' &&
        this.searchInputFilter === '' &&
        this.selectedTags.length
      ) {
        this.selectedTags.pop();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-admin-modal {
  :deep() .modal-dialog {
    max-width: 640px !important;
  }

  :deep() .modal-body {
    height: 80vh !important;
  }

  :deep() .modal-content {
    background-color: transparent !important;
    box-shadow: unset;
    -webkit-box-shadow: unset;
  }
}
.search-admin-wrapper {
  width: 100%;
}
.search-input {
  box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2), 0 5px 15px rgba(0, 0, 0, 0.17);
  -webkit-box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.17);
}
.search-dropdown {
  background: white;
  border-radius: 2px;
  box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2), 0 5px 15px rgba(0, 0, 0, 0.17);
  -webkit-box-shadow: 0 15px 35px rgba(50, 50, 93, 0.2),
    0 5px 15px rgba(0, 0, 0, 0.17);
}

.overflow {
  max-height: 65vh;
  overflow: auto;
}

.search-correction {
  margin-left: 4px;
  font-weight: 700;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  white-space: break-spaces;
  &:hover {
    color: $blue-mayday !important;
    text-decoration: underline !important;
  }
}

.search-autocomplete {
  margin-left: 4px;
  font-weight: 700;
  cursor: pointer;
}
</style>
